<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from "vue";
import skinImage from "@/assets/images/skin.jpg";

export interface Marker {
  id: string;
  number: number;
  x: number;
  y: number;
  description: string;
}

interface Props {
  modelValue?: Marker[];
  readonly?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => [],
  readonly: false,
});

const emit = defineEmits<{
  (e: "update:modelValue", value: Marker[]): void;
}>();

const containerRef = ref<HTMLDivElement | null>(null);
const imageRef = ref<HTMLImageElement | null>(null);
const markers = ref<Marker[]>([...props.modelValue]);
const selectedMarker = ref<Marker | null>(null);
const showDescriptionModal = ref(false);
const newMarkerPosition = ref<{ x: number; y: number } | null>(null);
const descriptionInput = ref("");
const imageLoaded = ref(false);

// Генерация уникального ID
const generateId = (): string => {
  return `marker_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Получение следующего номера метки
const getNextNumber = (): number => {
  if (markers.value.length === 0) return 1;
  return Math.max(...markers.value.map((m) => m.number)) + 1;
};

// Конвертация координат клика в проценты относительно размера изображения
const getRelativePosition = (event: MouseEvent): { x: number; y: number } => {
  if (!imageRef.value) return { x: 0, y: 0 };

  const rect = imageRef.value.getBoundingClientRect();
  const x = ((event.clientX - rect.left) / rect.width) * 100;
  const y = ((event.clientY - rect.top) / rect.height) * 100;

  return { x, y };
};

// Обработка клика по изображению
const handleImageClick = (event: MouseEvent) => {
  if (props.readonly) return;

  const target = event.target as HTMLElement;
  if (
    target.classList.contains("marker-point") ||
    target.closest(".marker-point")
  ) {
    return;
  }

  const position = getRelativePosition(event);
  newMarkerPosition.value = position;
  descriptionInput.value = "";
  showDescriptionModal.value = true;
};

// Подтверждение добавления метки
const confirmAddMarker = () => {
  if (!newMarkerPosition.value) return;

  const newMarker: Marker = {
    id: generateId(),
    number: getNextNumber(),
    x: newMarkerPosition.value.x,
    y: newMarkerPosition.value.y,
    description: descriptionInput.value,
  };

  markers.value.push(newMarker);
  emit("update:modelValue", [...markers.value]);
  closeDescriptionModal();
};

// Закрытие модального окна
const closeDescriptionModal = () => {
  showDescriptionModal.value = false;
  newMarkerPosition.value = null;
  descriptionInput.value = "";
  selectedMarker.value = null;
};

// Выбор метки для редактирования
const selectMarker = (marker: Marker) => {
  if (props.readonly) return;
  selectedMarker.value = marker;
  descriptionInput.value = marker.description;
  showDescriptionModal.value = true;
};

// Обновление описания метки
const updateMarkerDescription = () => {
  if (!selectedMarker.value) return;

  const index = markers.value.findIndex(
    (m) => m.id === selectedMarker.value!.id,
  );
  if (index !== -1) {
    markers.value[index].description = descriptionInput.value;
    emit("update:modelValue", [...markers.value]);
  }
  closeDescriptionModal();
};

// Удаление метки
const deleteMarker = (markerId: string) => {
  markers.value = markers.value.filter((m) => m.id !== markerId);
  // Перенумерация оставшихся меток
  markers.value.forEach((m, index) => {
    m.number = index + 1;
  });
  emit("update:modelValue", [...markers.value]);
  closeDescriptionModal();
};

// Обработка подтверждения в модальном окне
const handleModalConfirm = () => {
  if (selectedMarker.value) {
    updateMarkerDescription();
  } else {
    confirmAddMarker();
  }
};

// Обработка нажатия Escape
const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === "Escape" && showDescriptionModal.value) {
    closeDescriptionModal();
  }
};

onMounted(() => {
  document.addEventListener("keydown", handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeydown);
});

// Синхронизация с props.modelValue
const syncMarkers = () => {
  markers.value = [...props.modelValue];
};
</script>

<template>
  <div class="body-marker-container">
    <div class="image-wrapper" ref="containerRef">
      <img
        ref="imageRef"
        :src="skinImage"
        alt="Схема тела"
        class="body-image"
        @click="handleImageClick"
        @load="imageLoaded = true"
      />

      <!-- Метки на изображении -->
      <div
        v-for="marker in markers"
        :key="marker.id"
        class="marker-point"
        :class="{ selected: selectedMarker?.id === marker.id }"
        :style="{
          left: `${marker.x}%`,
          top: `${marker.y}%`,
        }"
        @click.stop="selectMarker(marker)"
      >
        <span class="marker-number">{{ marker.number }}</span>
        <div class="marker-tooltip" v-if="marker.description">
          {{ marker.description }}
        </div>
      </div>
    </div>

    <!-- Список меток -->
    <div class="markers-list">
      <h5 class="markers-list-title">Метки ({{ markers.length }}):</h5>
      <ul class="markers-list-items" v-if="markers.length > 0">
        <li v-for="marker in markers" :key="marker.id" class="marker-item">
          <span class="marker-item-number">{{ marker.number }}.</span>
          <span class="marker-item-description">{{
            marker.description || "Без описания"
          }}</span>
          <button
            v-if="!readonly"
            class="marker-item-delete"
            @click="deleteMarker(marker.id)"
            title="Удалить метку"
          >
            ×
          </button>
        </li>
      </ul>
      <div v-else class="markers-list-empty">
        Нажмите на изображение, чтобы добавить метку
      </div>
    </div>

    <!-- Модальное окно для ввода описания -->
    <div
      v-if="showDescriptionModal"
      class="modal-overlay"
      @click.self="closeDescriptionModal"
    >
      <div class="modal-content">
        <h4 class="modal-title">
          {{ selectedMarker ? "Редактировать метку" : "Новая метка" }}
          <span v-if="!selectedMarker && newMarkerPosition">
            №{{ getNextNumber() }}
          </span>
        </h4>
        <div class="modal-body">
          <label for="markerDescription">Описание:</label>
          <textarea
            id="markerDescription"
            v-model="descriptionInput"
            class="form-control"
            rows="3"
            placeholder="Введите описание метки"
            @keydown.enter.ctrl="handleModalConfirm"
            autofocus
          ></textarea>
        </div>
        <div class="modal-footer">
          <button
            v-if="selectedMarker"
            class="btn btn-danger"
            @click="deleteMarker(selectedMarker.id)"
          >
            Удалить
          </button>
          <div class="modal-footer-right">
            <button class="btn btn-secondary" @click="closeDescriptionModal">
              Отмена
            </button>
            <button class="btn btn-primary" @click="handleModalConfirm">
              {{ selectedMarker ? "Сохранить" : "Добавить" }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.body-marker-container {
  display: flex;
  flex-direction: row;
  gap: 1.5rem;
  align-items: flex-start;
}

.image-wrapper {
  position: relative;
  width: 100%;
  max-width: 600px;
}

.body-image {
  display: block;
  width: 100%;
  height: auto;
  max-width: 100%;
  user-select: none;
}

.marker-point {
  position: absolute;
  width: 24px;
  height: 24px;
  background-color: #dc3545;
  border: 2px solid #fff;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  transition: transform 0.2s, box-shadow 0.2s;
  z-index: 10;
}

.marker-point:hover {
  transform: translate(-50%, -50%) scale(1.2);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.4);
}

.marker-point.selected {
  background-color: #007bff;
  transform: translate(-50%, -50%) scale(1.3);
}

.marker-number {
  color: #fff;
  font-size: 11px;
  font-weight: bold;
  text-shadow: 0 1px 1px rgba(0, 0, 0, 0.3);
}

.marker-tooltip {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(0, 0, 0, 0.8);
  color: #fff;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s, visibility 0.2s;
  pointer-events: none;
  margin-bottom: 4px;
}

.marker-point:hover .marker-tooltip {
  opacity: 1;
  visibility: visible;
}

.markers-list {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 0.75rem;
  flex: 1;
  min-width: 250px;
  width: 100%;
  max-height: 500px;
  overflow-y: auto;
}

.markers-list-title {
  margin: 0 0 0.5rem 0;
  font-size: 14px;
  font-weight: 600;
}

.markers-list-items {
  list-style: none;
  padding: 0;
  margin: 0;
}

.markers-list-empty {
  color: #6c757d;
  font-size: 13px;
  font-style: italic;
}

.marker-item {
  display: flex;
  align-items: flex-start;
  padding: 0.25rem 0;
  border-bottom: 1px solid #e9ecef;
}

.marker-item:last-child {
  border-bottom: none;
}

.marker-item-number {
  font-weight: 600;
  color: #dc3545;
  margin-right: 0.5rem;
  min-width: 20px;
}

.marker-item-description {
  flex: 1;
  font-size: 13px;
  color: #495057;

  min-width: 0;
  overflow-wrap: break-word;
  word-break: break-word;
  white-space: normal;
}

.marker-item-delete {
  background: none;
  border: none;
  color: #dc3545;
  font-size: 18px;
  cursor: pointer;
  padding: 0 0.25rem;
  opacity: 0.6;
  transition: opacity 0.2s;
}

.marker-item-delete:hover {
  opacity: 1;
}

/* Модальное окно */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
}

.modal-content {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  width: 90%;
  max-width: 400px;
  overflow: hidden;
}

.modal-title {
  margin: 0;
  padding: 1rem;
  font-size: 16px;
  font-weight: 600;
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.modal-body {
  padding: 1rem;
}

.modal-body label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.modal-body textarea {
  width: 100%;
  resize: vertical;
}

.modal-footer {
  padding: 1rem;
  background-color: #f8f9fa;
  border-top: 1px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-footer-right {
  display: flex;
  gap: 0.5rem;
}

.btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
}

.btn-primary {
  background-color: #007bff;
  color: #fff;
}

.btn-primary:hover {
  background-color: #0056b3;
}

.btn-secondary {
  background-color: #6c757d;
  color: #fff;
}

.btn-secondary:hover {
  background-color: #545b62;
}

.btn-danger {
  background-color: #dc3545;
  color: #fff;
}

.btn-danger:hover {
  background-color: #c82333;
}

@media (max-width: 1040px) {
  .body-marker-container {
    display: flex;
    flex-direction: column;
    gap: 10px;
    align-items: center;
    margin-bottom: 6px;
  }

  .markers-list {
    width: 100%;
  }
}
</style>
